import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CookieConsent, ConsentUpdate, ConsentCategory, BannerState } from '@/types/consent';
import { CookieConsentManager } from '@/lib/cookieConsent';
import { GoogleAnalytics } from '@/lib/analytics';
import { GA_MEASUREMENT_ID, IS_DEV, COOKIE_CONSENT_KEY } from '@/config/analytics';

interface CookieConsentContextType {
  consent: CookieConsent | null;
  bannerState: BannerState;
  updateConsent: (updates: ConsentUpdate) => void;
  showBanner: () => void;
  hideBanner: () => void;
  toggleBanner: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  acceptSelected: (categories: ConsentCategory[]) => void;
  hasConsent: (category: ConsentCategory) => boolean;
  isFirstVisit: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
  showBannerOnLoad?: boolean;
  autoLoadAnalytics?: boolean;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({
  children,
  showBannerOnLoad = true,
  autoLoadAnalytics = true,
}) => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [bannerState, setBannerState] = useState<BannerState>('hidden');
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  // Initialize consent on mount
  useEffect(() => {
    const initialConsent = CookieConsentManager.getConsent();
    setConsent(initialConsent);
    setIsFirstVisit(CookieConsentManager.isFirstVisit());

    // Show banner on first visit if configured
    if (showBannerOnLoad && CookieConsentManager.isFirstVisit()) {
      setBannerState('expanded');
    } else if (!CookieConsentManager.isFirstVisit()) {
      // Show minimized banner for returning users
      setBannerState('minimized');
    }

    if (IS_DEV) {
      console.log('CookieConsentProvider initialized:', {
        consent: initialConsent,
        isFirstVisit: CookieConsentManager.isFirstVisit(),
        bannerState: bannerState,
      });
    }
  }, [showBannerOnLoad]);

  // Handle analytics loading based on consent
  useEffect(() => {
    if (!consent || !autoLoadAnalytics) return;

    if (consent.analytics === 'granted' && GA_MEASUREMENT_ID) {
      GoogleAnalytics.initialize(GA_MEASUREMENT_ID);
      
      // Track consent given event
      GoogleAnalytics.trackEvent('consent', 'analytics_granted', 'initial_load');
      
      if (IS_DEV) {
        console.log('Analytics initialized with consent:', consent.analytics);
      }
    } else if (consent.analytics === 'denied') {
      if (IS_DEV) {
        console.log('Analytics not loaded due to consent denial');
      }
    }
  }, [consent?.analytics, autoLoadAnalytics]);

  // Listen for consent updates from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === COOKIE_CONSENT_KEY) {
        const newConsent = CookieConsentManager.getConsent();
        setConsent(newConsent);
        
        if (IS_DEV) {
          console.log('Consent updated from storage event:', newConsent);
        }
      }
    };

    const handleConsentUpdated = (e: CustomEvent) => {
      setConsent(e.detail);
      
      if (IS_DEV) {
        console.log('Consent updated from custom event:', e.detail);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('consentUpdated', handleConsentUpdated as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('consentUpdated', handleConsentUpdated as EventListener);
    };
  }, []);

  const updateConsent = (updates: ConsentUpdate) => {
    if (!consent) return;

    const previousConsent = { ...consent };
    const newConsent = CookieConsentManager.updateConsent(updates);
    setConsent(newConsent);

    // Track consent changes
    if (updates.analytics && updates.analytics !== previousConsent.analytics) {
      const action = updates.analytics === 'granted' ? 'analytics_granted' : 'analytics_revoked';
      GoogleAnalytics.trackEvent('consent', action, 'user_update');
    }

    // Hide banner after consent is updated
    if (bannerState === 'expanded') {
      setBannerState('minimized');
    }

    if (IS_DEV) {
      console.log('Consent updated:', { previous: previousConsent, updates, new: newConsent });
    }
  };

  const showBanner = () => {
    setBannerState('expanded');
    GoogleAnalytics.trackEvent('consent', 'banner_shown', 'manual_trigger');
  };

  const hideBanner = () => {
    setBannerState('hidden');
    GoogleAnalytics.trackEvent('consent', 'banner_hidden', 'user_action');
  };

  const toggleBanner = () => {
    setBannerState(prev => prev === 'expanded' ? 'minimized' : 'expanded');
    GoogleAnalytics.trackEvent('consent', 'banner_toggled', bannerState);
  };

  const acceptAll = () => {
    updateConsent({
      analytics: 'granted',
      marketing: 'granted',
    });
    GoogleAnalytics.trackEvent('consent', 'all_accepted', 'banner_action');
  };

  const rejectAll = () => {
    updateConsent({
      analytics: 'denied',
      marketing: 'denied',
    });
    GoogleAnalytics.trackEvent('consent', 'all_rejected', 'banner_action');
  };

  const acceptSelected = (categories: ConsentCategory[]) => {
    const updates: ConsentUpdate = {};
    
    categories.forEach(category => {
      if (category === 'essential') return; // Essential is always granted
      updates[category] = 'granted';
    });

    // Deny categories not in the list (except essential)
    const allCategories: ConsentCategory[] = ['essential', 'analytics', 'marketing'];
    allCategories.forEach(category => {
      if (category !== 'essential' && !categories.includes(category)) {
        updates[category] = 'denied';
      }
    });

    updateConsent(updates);
    GoogleAnalytics.trackEvent('consent', 'selected_accepted', categories.join(','));
  };

  const hasConsent = (category: ConsentCategory): boolean => {
    if (!consent) return false;
    return consent[category] === 'granted';
  };

  const value: CookieConsentContextType = {
    consent,
    bannerState,
    updateConsent,
    showBanner,
    hideBanner,
    toggleBanner,
    acceptAll,
    rejectAll,
    acceptSelected,
    hasConsent,
    isFirstVisit,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Convenience hook for checking analytics consent
export const useAnalyticsConsent = (): boolean => {
  const { hasConsent } = useCookieConsent();
  return hasConsent('analytics');
};

// Convenience hook for banner control
export const useCookieBanner = () => {
  const { bannerState, showBanner, hideBanner, toggleBanner } = useCookieConsent();
  return { bannerState, showBanner, hideBanner, toggleBanner };
};
